/**
 * Links acortados y clics (Link Shortening, §B.7.6), en Configuración → «Conexión (técnico)».
 *
 * Estaba en «Envío», entre la franja horaria y el idioma, como si fuera una decisión más del día a
 * día. No lo es: apagado por defecto, cuesta aparte y sólo anda con un dominio propio configurado en
 * la consola de Twilio (sin él, Twilio rechaza el envío y el backend devuelve 422). Vive al lado de
 * la guía de la consola, cuyo paso «(Opcional) Link Shortening» manda acá.
 *
 * Guarda sola, con su propio botón (`PATCH config { linkShortening }`, sólo si cambió), con el mismo
 * formulario que las secciones (`useFormularioConfig`): editar acá no pisa lo que se cambie en otra.
 * Las dos casillas quedan deshabilitadas con el interruptor apagado, pero conservan su valor: al
 * volver a prenderlo, se respeta lo elegido.
 */

import { useId } from "react";
import { AlertTriangle, Link2 } from "lucide-react";
import { linkShorteningDe } from "../../logic/config";
import { TEXTO_CLICS_VISTAS_PREVIAS, TEXTO_LINK_SHORTENING, TEXTO_LINK_SHORTENING_CAMPANAS } from "../../logic/textos";
import { Tarjeta } from "../../components/Tarjeta";
import { BotoneraGuardar, useFormularioConfig } from "./SeccionEnvio";

export function TarjetaLinkShortening() {
  const f = useFormularioConfig();
  const idSwitch = useId();
  if (!f.editado) return null;
  const valor = linkShorteningDe(f.editado);
  const cambiar = (v: typeof valor) => f.set("linkShortening", v);

  return (
    <Tarjeta titulo="Links acortados y clics (Link Shortening)" right={<Link2 size={16} className="text-slate-400" />}>
      <div className="space-y-4">
        <p className="text-sm text-slate-600">{TEXTO_LINK_SHORTENING}</p>
        <div className="flex items-center gap-3">
          <button id={idSwitch} type="button" role="switch" aria-checked={valor.activo} onClick={() => cambiar({ ...valor, activo: !valor.activo })}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 ${valor.activo ? "bg-violet-600" : "bg-slate-300"}`}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${valor.activo ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
          <label htmlFor={idSwitch} className="text-sm font-medium text-slate-800 cursor-pointer">
            {valor.activo ? "Acortar los links y contar los clics" : "Apagado"}
          </label>
        </div>
        <fieldset disabled={!valor.activo} className="space-y-2 pl-1 disabled:opacity-50">
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={valor.enSesiones} onChange={(ev) => cambiar({ ...valor, enSesiones: ev.target.checked })}
              className="mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
            <span>En mensajes de servicio (las respuestas dentro de las 24 h)</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={valor.enPlantillas} onChange={(ev) => cambiar({ ...valor, enPlantillas: ev.target.checked })}
              className="mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
            <span>En campañas (plantillas aprobadas)</span>
          </label>
          {valor.activo && valor.enPlantillas && (
            <p className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
              <AlertTriangle size={13} className="flex-shrink-0 mt-px" /> {TEXTO_LINK_SHORTENING_CAMPANAS}
            </p>
          )}
        </fieldset>
        <p className="text-xs text-slate-400">{TEXTO_CLICS_VISTAS_PREVIAS}</p>
        <BotoneraGuardar f={f} />
      </div>
    </Tarjeta>
  );
}
